import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Upload,
  FileSpreadsheet,
  Download,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { bulkCreateLeads } from '../../lib/api';

const SAMPLE_CSV = `business_name,contact_name,phone,email,location,business_type,message
Acme Plumbing,John Smith,+61400111222,john@acme.com,"Sydney, NSW",plumber,Interested in AI receptionist
Best Electric,Jane Doe,+61400333444,jane@bestelectric.com,"Melbourne, VIC",electrician,Called about appointment booking
Clean Home Services,Bob Wilson,+61400555666,,Brisbane QLD,cleaner,Website inquiry`;

const BUSINESS_TYPES = [
  'restaurant', 'healthcare', 'real_estate', 'legal', 'retail', 'services',
  'plumber', 'electrician', 'hvac', 'cleaner', 'landscaper', 'contractor',
  'carpenter', 'painter', 'roofer', 'locksmith', 'pest_control', 'auto_repair', 'other'
];

export default function BulkUploadPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [preview, setPreview] = useState(null);

  const downloadSampleCSV = () => {
    const blob = new Blob([SAMPLE_CSV], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'leads_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV must have a header row and at least one data row');
    }

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
    const requiredHeaders = ['business_name', 'phone', 'business_type'];
    const missingHeaders = requiredHeaders.filter(h => !headers.includes(h));
    
    if (missingHeaders.length > 0) {
      throw new Error(`Missing required columns: ${missingHeaders.join(', ')}`);
    }

    const leads = [];
    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i]);
      if (values.length !== headers.length) {
        continue; // Skip malformed rows
      }

      const lead = {};
      headers.forEach((header, index) => {
        lead[header] = values[index]?.trim() || '';
      });
      leads.push(lead);
    }

    return leads;
  };

  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current);
    return result;
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target?.result;
        const leads = parseCSV(text);
        
        if (leads.length === 0) {
          toast.error('No valid leads found in CSV');
          return;
        }

        if (leads.length > 500) {
          toast.error('Maximum 500 leads per upload');
          return;
        }

        setPreview({
          total: leads.length,
          leads: leads.slice(0, 5),
          allLeads: leads
        });
        setResults(null);
      } catch (error) {
        toast.error(error.message || 'Failed to parse CSV');
      }
    };
    reader.readAsText(file);
  };

  const handleUpload = async () => {
    if (!preview?.allLeads) return;

    setUploading(true);
    try {
      const response = await bulkCreateLeads(preview.allLeads, 'csv_upload');
      setResults(response.data.results);
      setPreview(null);
      
      if (response.data.results.success > 0) {
        toast.success(`Successfully created ${response.data.results.success} leads`);
      }
      if (response.data.results.failed > 0) {
        toast.warning(`${response.data.results.failed} leads failed to import`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload leads');
    } finally {
      setUploading(false);
    }
  };

  const resetUpload = () => {
    setPreview(null);
    setResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-6" data-testid="bulk-upload-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/sales/leads')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Leads
        </Button>
      </div>

      <div className="max-w-3xl">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-indigo-600" />
              Bulk Upload Leads
            </CardTitle>
            <CardDescription>
              Upload multiple leads at once using a CSV file
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Instructions */}
            <div className="p-4 bg-slate-50 rounded-lg space-y-3">
              <h4 className="font-medium text-slate-900">CSV Format Requirements</h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• <strong>Required columns:</strong> business_name, phone, business_type</li>
                <li>• <strong>Optional columns:</strong> contact_name, email, location, message</li>
                <li>• <strong>Business types:</strong> {BUSINESS_TYPES.slice(0, 5).join(', ')}... and more</li>
                <li>• Maximum 500 leads per upload</li>
              </ul>
              <Button variant="outline" size="sm" onClick={downloadSampleCSV}>
                <Download className="w-4 h-4 mr-2" />
                Download Sample Template
              </Button>
            </div>

            {/* Upload Area */}
            {!preview && !results && (
              <div 
                className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-indigo-400 transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="csv-file-input"
                />
                <FileSpreadsheet className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                <p className="text-slate-600 mb-2">Click to select a CSV file</p>
                <p className="text-sm text-slate-400">or drag and drop</p>
              </div>
            )}

            {/* Preview */}
            {preview && !results && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-slate-900">Preview</h4>
                    <p className="text-sm text-slate-500">{preview.total} leads ready to import</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={resetUpload}>
                    Choose Different File
                  </Button>
                </div>

                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left py-2 px-3">Business Name</th>
                        <th className="text-left py-2 px-3">Contact</th>
                        <th className="text-left py-2 px-3">Phone</th>
                        <th className="text-left py-2 px-3">Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.leads.map((lead, i) => (
                        <tr key={i} className="border-t">
                          <td className="py-2 px-3">{lead.business_name}</td>
                          <td className="py-2 px-3">{lead.contact_name || '-'}</td>
                          <td className="py-2 px-3">{lead.phone}</td>
                          <td className="py-2 px-3 capitalize">{lead.business_type?.replace('_', ' ')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {preview.total > 5 && (
                    <div className="bg-slate-50 py-2 px-3 text-sm text-slate-500">
                      ... and {preview.total - 5} more leads
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3">
                  <Button variant="outline" onClick={resetUpload}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleUpload}
                    disabled={uploading}
                    className="bg-indigo-600 hover:bg-indigo-700"
                    data-testid="upload-btn"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Upload {preview.total} Leads
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Results */}
            {results && (
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                  <div className="flex-1">
                    <h4 className="font-medium text-slate-900">Upload Complete</h4>
                    <div className="flex items-center gap-6 mt-2">
                      <div className="flex items-center gap-2 text-green-600">
                        <CheckCircle2 className="w-5 h-5" />
                        <span>{results.success} created</span>
                      </div>
                      {results.failed > 0 && (
                        <div className="flex items-center gap-2 text-red-600">
                          <XCircle className="w-5 h-5" />
                          <span>{results.failed} failed</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <Progress 
                    value={(results.success / (results.success + results.failed)) * 100} 
                    className="w-32"
                  />
                </div>

                {results.errors?.length > 0 && (
                  <div className="border border-red-200 bg-red-50 rounded-lg p-4">
                    <h4 className="font-medium text-red-800 flex items-center gap-2 mb-2">
                      <AlertCircle className="w-4 h-4" />
                      Import Errors
                    </h4>
                    <ul className="text-sm text-red-700 space-y-1 max-h-40 overflow-y-auto">
                      {results.errors.slice(0, 10).map((error, i) => (
                        <li key={i}>Row {error.row}: {error.error}</li>
                      ))}
                      {results.errors.length > 10 && (
                        <li className="text-red-500">... and {results.errors.length - 10} more errors</li>
                      )}
                    </ul>
                  </div>
                )}

                <div className="flex justify-end gap-3">
                  <Button variant="outline" onClick={resetUpload}>
                    Upload More
                  </Button>
                  <Button 
                    onClick={() => navigate('/sales/leads')}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    View All Leads
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
